/**
 * Shared configuration options for the Automatic AD and Automatic Scene Descriptions modals.
 *
 * Both modals (and the back-end task pipeline) read from a single source of truth for the
 * language/length/focus options so the UI and prompt builders cannot drift apart.
 * The cloud functions mirror this list in `functions/lib/aiAdOptions.js` because they
 * cannot import from `src/`.
 */

/**
 * Languages the LLM may emit Audio Descriptions and Scene Descriptions in.
 * Codes use locale-style tags where the customer cares about the regional variant
 * (Castilian Spanish vs Latin-American Spanish; Catalan as its own language).
 */
export const OUTPUT_LANGUAGES = [
    { code: "en", label: "English" },
    { code: "it", label: "Italian" },
    { code: "de", label: "German" },
    { code: "es", label: "Spanish (Global)" },
    { code: "es-ES", label: "Spanish (Castilian)" },
    { code: "ca", label: "Catalan" },
    { code: "cs", label: "Czech" },
    { code: "fr", label: "French" },
    { code: "pt-PT", label: "Portuguese (Portugal)" },
    { code: "ja", label: "Japanese" },
    { code: "zh-CN", label: "Chinese (Simplified)" },
    { code: "hi", label: "Hindi" },
    { code: "ru", label: "Russian" }
];

/**
 * Length presets that control both prompt-level word budgets and provider-level token caps.
 * Customer feedback explicitly asked for a way to keep Scene Descriptions concise, so
 * "Standard" is the default and is tighter than the v1 hardcoded 500/1000-token budgets.
 */
export const DESCRIPTION_LENGTH_PRESETS = {
    brief: { label: "Brief (\u2264 12 words)", maxWords: 12, maxTokens: 60 },
    standard: { label: "Standard (\u2264 25 words)", maxWords: 25, maxTokens: 120 },
    detailed: { label: "Detailed (\u2264 45 words)", maxWords: 45, maxTokens: 220 }
};

/**
 * Focus options the LLM may be asked to emphasise when describing a scene.
 * The `id` values are persisted in job configs; the `label` is only used for the UI.
 */
export const DESCRIPTION_FOCUS_OPTIONS = [
    { id: "actions", label: "Actions and movements" },
    { id: "characters", label: "Characters and expressions" },
    { id: "setting", label: "Setting and environment" },
    { id: "shotType", label: "Shot type (close-up, wide, etc.)" },
    { id: "onScreenText", label: "On-screen text and titles" },
    { id: "props", label: "Props and costumes" }
];

/** Focus list applied when a job document (v1) does not specify one. */
export const DEFAULT_FOCUS = ["actions", "characters", "setting", "onScreenText"];

/** Trial accounts cannot submit videos longer than this duration (in seconds). */
export const TRIAL_MAX_DURATION_SECONDS = 300;

/**
 * Comfortable baseline speech rate (words per minute) for audio description.
 *
 * 150 WPM is the broadcast convention for AD. Every per-event speed calculation uses
 * this as the floor: an event whose script fits inside `duration * 150 / 60` words
 * always plays back at speed 1.0. Only events the LLM had to pack more words into
 * (because the time window was tight) get sped up.
 */
export const BASELINE_WPM = 150;

/**
 * Max-reading-rate presets for the Automatic AD pipeline.
 *
 * The selected WPM is a *ceiling*, not a target: the LLM is told it may use anywhere
 * from `BASELINE_WPM` up to this many words per minute, and the playback `speed` is
 * then computed per event from the actual word count. Selecting 150 collapses the
 * range to a single value and keeps every event at speed 1.0 (v1 behaviour).
 */
export const MAX_WPM_PRESETS = [
    { wpm: 150, rate: 1.0, label: "150 WPM (max 1.00x, default)" },
    { wpm: 200, rate: 1.33, label: "200 WPM (max 1.33x)" },
    { wpm: 250, rate: 1.67, label: "250 WPM (max 1.67x)" },
    { wpm: 300, rate: 2.0, label: "300 WPM (max 2.00x)" }
];

/** Resolve a preset by max-WPM value. Falls back to the 150 WPM baseline. */
export function getMaxWpmPreset(wpm) {
    return MAX_WPM_PRESETS.find(p => p.wpm === Number(wpm))
        || MAX_WPM_PRESETS[0];
}

/** Convenience helper to resolve a display label for an output language code. */
export function getOutputLanguageLabel(code) {
    const match = OUTPUT_LANGUAGES.find(l => l.code === code);
    return match ? match.label : code;
}

/**
 * Clamp a [start, end] segment selection (seconds) against the available video
 * duration. Returns a normalised `{ start, end, duration }` object. Used by both
 * the AD and SD modals (to drive ffmpeg `-ss` / `-t`) and the cloud-function
 * config reader (to resolve effective bounds).
 *
 * Defaults: missing or non-numeric `startSec` becomes 0; missing `endSec` becomes
 * the full duration. Both inputs are clamped into [0, durationSec], and `end` is
 * never allowed to fall below `start` so callers can safely subtract.
 */
export function clampSegment(startSec, endSec, durationSec) {
    const totalDur = Math.max(0, Number(durationSec) || 0);
    const sRaw = Number(startSec);
    const s = Math.max(0, Math.min(totalDur, isFinite(sRaw) ? sRaw : 0));
    const eRaw = endSec == null ? totalDur : Number(endSec);
    const e = Math.max(s, Math.min(totalDur, isFinite(eRaw) ? eRaw : totalDur));
    return { start: s, end: e, duration: e - s };
}
